import { useRouter } from 'next/router';
import React, { ReactElement } from 'react';
import { useNotification } from './useNotification';
import { customEvent, formatErrorMessage } from '@/lib';
import { ErrorHttpResponse } from '@/interfaces';
import AppRoutes from '@/const/routes';
import { authService, verificationsService } from '@/api';
import {
	deleteLocalStorage,
	getLocalStorage,
	setLocalStorage,
} from '@/utils/localStorage';
import { getSession, signIn } from 'next-auth/react';
import NextAuthProvidersEnum from '@/const/auth';
import { FieldValues } from 'react-hook-form';
import Logger from '@/utils/logger';
import { encrypt } from '@/lib/crypto';
import { Typography } from '@/components/common/typography/typography';

export const useSignIn = () => {
	const { toastError, toastSuccess } = useNotification();
	const router = useRouter();
	const { pathname, query } = router;
	const { email, statusCode, message, evaluated, case_id } = router.query;
	const logger = new Logger({ identifier: 'SignIn' });

	// utils
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [showDeactivatedMessage, setShowDeactivatedMessage] =
		React.useState(false);
	const [showReactivateAccountLink, setShowReactivateAccountLink] =
		React.useState(false);
	const [isSocialRecover, setIsSocialRecover] = React.useState<boolean>(false);
	const [isSocialReactivated, setIsSocialReactivated] = React.useState(false);
	const [isLoadingScreen, setIsLoadingScreen] = React.useState(true);
	const [errorMessage, setErrorMessage] = React.useState<
		string | ReactElement
	>();
	const [isVerified, setIsVerified] = React.useState<boolean>(true);

	const socialProviders = ['google', 'microsoft'];

	const handleRecoverSocialAccount = async (): Promise<void> => {
		setIsSocialRecover(false);
		setIsLoadingScreen(true);
		try {
			setIsLoadingScreen(false);
			setIsSocialReactivated(true);
			const response = await authService.recoverSocialAccount(email as string);
			deleteLocalStorage('social-recover');

			setTimeout(async () => {
				await signIn('recovery-token', {
					user: JSON.stringify(response),
					callbackUrl: `${AppRoutes.CLIENTAPP_DASHBOARD}?confirm-account=reactivated`,
				});
			}, 3000);
		} catch (error) {
			const status = (error as ErrorHttpResponse).statusCode;
			if (status !== 409) {
				toastError(formatErrorMessage((error as any).message));
			}
		} finally {
			setIsLoadingScreen(false);
			// remove code param
			delete query['code'];
			router.replace({
				pathname,
				query,
			});
		}
	};

	const handleShowErrors = React.useCallback((errorMessage: string) => {
		const errorMsg = errorMessage || 'Error, try again';
		if (
			errorMessage.includes('verify') ||
			errorMessage.startsWith('Would you like to reactivate')
		) {
			setErrorMessage(errorMsg);
			setIsVerified(false);
			return deleteQuery();
		}

		if (errorMessage.includes('reactivate')) {
			setShowReactivateAccountLink(true);
			return deleteQuery();
		}

		if (errorMessage.includes('deactivate')) {
			setShowDeactivatedMessage(true);
			return deleteQuery();
		}

		toastError(errorMsg);
		deleteQuery();
	}, []);

	const showErrorsRender = React.useCallback(() => {
		const currentProvider = getLocalStorage('provider');
		if (statusCode === '400') {
			setErrorMessage(
				<Typography type="regular-16" className="text-main-red">
					It looks like you signed in with your Gmail or Microsoft account using
					an email and password instead of selecting{' '}
					<b>&quot;Sign in with Google&quot;</b> or{' '}
					<b>&quot;Sign in with Microsoft&quot;</b>.
					<br /> <br />
					To access your account, please{' '}
					<b>&quot;Sign In With Email and Password&quot;</b>
				</Typography>
			);
		} else if (statusCode === '401') {
			setErrorMessage(
				'Please verify your email address. Check your inbox or spam for a verification email from us.'
			);
		} else if (
			statusCode === '409' &&
			message === 'reactivate' &&
			socialProviders.includes(currentProvider)
		) {
			const recoverSocial = getLocalStorage('social-recover');
			recoverSocial ? handleRecoverSocialAccount() : setIsSocialRecover(true);
		} else if (statusCode === '409' && message === 'reactivate') {
			setShowReactivateAccountLink(true);
		} else if (statusCode === '409' && message === 'deactivate') {
			setShowDeactivatedMessage(true);
		}

		setTimeout(() => {
			setIsLoadingScreen(false);
		}, 1000);
		if (statusCode || message) deleteQuery();
	}, [statusCode, message, handleRecoverSocialAccount]);

	const deleteQuery = (): void => {
		delete query['verify-error'];
		delete query['callbackUrl'];
		delete query['error'];
		delete query['message'];
		delete query['statusCode'];
		// remove params
		router.replace({
			pathname,
			query,
		});
	};

	const handleBack = (): void => {
		deleteLocalStorage('social-recover');
		setIsSocialRecover(false);
		delete query['email'];
		// remove params
		router.replace({
			pathname,
			query,
		});
	};

	const redirectBeforeLogin = async (): Promise<void> => {
		const session = await getSession();
		let redirectRoute: string = session?.user?.user?.is_2fa_actived
			? AppRoutes.CLIENTAPP_AUTH_TWO_FACTOR
			: AppRoutes.CLIENTAPP_DASHBOARD;

		if (evaluated && case_id) {
			redirectRoute = `${redirectRoute}?evaluated=${evaluated}&case_id=${case_id}`;
		}

		if (session?.user?.user?.is_2fa_actived) {
			await verificationsService.verifyNumber('2fa');
			router.push(redirectRoute);
		} else {
			router.push(redirectRoute);
		}
	};

	const registerFirstSignin = (): void => {
		if (!getLocalStorage('first_signin')) {
			setLocalStorage('first_signin', true);
			customEvent({
				action: 'first_sign_in_click',
				category: 'landing',
				label: 'First Sign-in',
				value: 'First Sign-in',
			});
		}
	};

	const handleSendVerifyEmail = async (): Promise<void> => {
		if (isLoading) return;
		setIsLoading(true);
		if (!email) {
			toastError('Email not found');
			return;
		}
		try {
			await authService.resendVerificationEmail(email as string);
			toastSuccess('Verification email has been successfully sent.');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoading(false);
		}
	};

	const handleBackSocialReactivated = () => {
		setIsSocialRecover(false);
		setIsSocialReactivated(false);
	};

	// SUBMIT function
	const handleSubmitData = React.useCallback(
		async (provider: NextAuthProvidersEnum, data?: FieldValues) => {
			setIsLoading(true);
			logger.info('Data: ', { data });
			setErrorMessage(undefined);
			setIsVerified(true);
			setLocalStorage('provider', provider);

			// remember me
			setLocalStorage('client_rememberme', data?.rememberme);
			if (data && data.rememberme) {
				const passwordEncrypted = encrypt(data.password);
				setLocalStorage('client_password', passwordEncrypted);
				setLocalStorage('client_email', data.email);
			}
			if (data && !data?.rememberme) {
				deleteLocalStorage('client_password');
				deleteLocalStorage('client_email');
			}
			try {
				if (provider === NextAuthProvidersEnum.CREDENTIALS) {
					registerFirstSignin();
					const resSignIn = await signIn(NextAuthProvidersEnum.CREDENTIALS, {
						...data,
						redirect: false, // false -> need to show the errors returned by backend
					});
					// check errors
					if (resSignIn && resSignIn.status !== 200) {
						handleShowErrors(resSignIn.error || '');
					} else {
						sessionStorage.removeItem('user_id_application');
						redirectBeforeLogin();
					}
				} else {
					// sign in with Google/Microsoft
					try {
						if (case_id && evaluated) {
							setLocalStorage('evaluated', {
								evaluated,
								case_id,
							});
						}
						registerFirstSignin();
						sessionStorage.removeItem('user_id_application');
						await signIn(provider, {
							redirect: false,
							callbackUrl: `${AppRoutes.CLIENTAPP_AUTH_TWO_FACTOR}`,
						});
					} catch (error) {
						toastError(error as string);
					}
				}
			} catch (error) {
				logger.error(error);
				toastError(error as string);
			} finally {
				setIsLoading(false);
			}
		},
		[handleShowErrors, redirectBeforeLogin]
	);

	return {
		showDeactivatedMessage,
		showReactivateAccountLink,
		isSocialRecover,
		isSocialReactivated,
		isLoadingScreen,
		errorMessage,
		isVerified,
		isLoading,
		showErrorsRender,
		handleBack,
		handleSubmitData,
		handleSendVerifyEmail,
		handleBackSocialReactivated,
	};
};
